/**
 * SegmentVM — ViewModel wrapping a segment entity ID and a Thread reference.
 *
 * Segments live as applogs in the thread (at `segment/startMs`, `annotation/transcript`, etc.),
 * NOT as note3 block entities. This VM provides reactive getters for all segment/annotation
 * attributes and commit methods that write applogs via `insertApplogs()`.
 */

import type { ApplogForInsertOptionalAgent, EntityID } from '@wovin/core/applog'
import type { Thread } from '@wovin/core/thread'
import { lastWriteWins, liveEntityAt } from '@wovin/core/query'
import { useEternalSubscribableValue } from '@wovin/hooks-solid'
import { insertApplogs } from '../../../data/ApplogDB'

export class SegmentVM {
  private _signals = new Map<string, () => any>()
  public readonly segmentId: EntityID
  private readonly thread: Thread

  constructor(segmentId: EntityID, thread: Thread) {
    this.segmentId = segmentId
    this.thread = thread
  }

  // ── Reactive attribute readers ──────────────────────────────────

  private _reactive(at: string) {
    let signal = this._signals.get(at)
    if (!signal) {
      const lww = lastWriteWins(this.thread, { tolerateAlreadyFiltered: true })
      signal = useEternalSubscribableValue(liveEntityAt(lww, this.segmentId, at))
      this._signals.set(at, signal)
    }
    return signal()
  }

  get transcript(): string | null {
    return this._reactive('annotation/transcript') as string | null
  }

  get speakerLabel(): string | null {
    const diarization = this._reactive('annotation/diarization') as string | null
    if (diarization) {
      try {
        const parsed = JSON.parse(diarization)
        return parsed.label || diarization
      } catch {
        return diarization
      }
    }
    return null
  }

  get speakerId(): string | null {
    return this._reactive('annotation/speakerId') as string | null
  }

  get speakerConfidence(): number | null {
    const val = this._reactive('annotation/speakerConfidence')
    return val != null ? Number(val) : null
  }

  get speakerSource(): string | null {
    return this._reactive('annotation/speakerSource') as string | null
  }

  get channelDominanceHint(): string | null {
    return this._reactive('annotation/channelDominance') as string | null
  }

  get startMs(): number {
    const val = this._reactive('segment/startMs')
    return val != null ? Number(val) : 0
  }

  get endMs(): number {
    const val = this._reactive('segment/endMs')
    return val != null ? Number(val) : 0
  }

  get isDeleted(): boolean {
    return this._reactive('annotation/isDeleted') === true
  }

  get isEdited(): boolean {
    return this._reactive('annotation/isEdited') === true
  }

  // ── Write methods ───────────────────────────────────────────────

  /** Write a new annotation/transcript applog */
  commitEdit(newText: string): void {
    if (!this.thread) throw new Error('SegmentVM: no thread reference')
    void insertApplogs(this.thread, {
      en: this.segmentId,
      at: 'annotation/transcript',
      vl: newText,
    } as ApplogForInsertOptionalAgent)
  }

  /** Write annotation/speakerId and annotation/diarization applogs */
  commitSpeaker(speakerId: string, label: string, source: string): void {
    if (!this.thread) throw new Error('SegmentVM: no thread reference')
    void insertApplogs(this.thread, [
      {
        en: this.segmentId,
        at: 'annotation/speakerId',
        vl: speakerId,
      },
      {
        en: this.segmentId,
        at: 'annotation/diarization',
        vl: JSON.stringify({ id: speakerId, label, source }),
      },
      {
        en: this.segmentId,
        at: 'annotation/speakerSource',
        vl: source,
      },
    ] as ApplogForInsertOptionalAgent[])
  }
}

/** Hook-style helper: returns a new or cached SegmentVM bound to a thread */
export function useTranscriptSegment(segmentId: string, thread: Thread): SegmentVM {
  // Simple memo — since VM constructors are cheap and SolidJS calls this once,
  // we just create a new instance. The thread reference is stable.
  return new SegmentVM(segmentId, thread)
}
